import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom"; // Add this import for navigation
import Header from "./components/career/Header";
import HeaderBanner from "./components/career/HeaderBanner";
import JobService from "./services/JobService";
import JobseekerService from "./services/JobseekerService";
import { API_BASE_URL } from "./utils/api";
import { capitalizeWords } from "./utils";
import {
  Clock,
  MapPin,
  Building,
  Calendar,
  Briefcase,
  CreditCard,
  Award,
  FileText,
  CheckCircle,
  User,
  Info,
  ArrowLeft,
} from "lucide-react";
import { JobDescriptionHtmlParser } from "./utils";

const ApplicationForm = ({ onClose, jobId }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    postcode: "",
    resume: {},
    resumeSelected: false,
    job: jobId,
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileInput = (e) => {
    fileInputRef.current.click();
  };

  const handleFileSelect = (e) => {
    setFormData((prev) => ({
      ...prev,
      resumeSelected: true,
      resume: e.target.files,
    }));
  };

  const validateFormData = (formData, termsAccepted) => {
    const errors = [];

    const ukPhoneRegex = /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/;
    const ukPostcodeRegex = /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i;

    if (!formData.fullName.trim()) errors.push("Full name is required.");

    if (!formData.email.trim()) {
      errors.push("Email is required.");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.push("Email is invalid.");
    }

    if (!formData.phone.trim()) {
      errors.push("Phone number is required.");
    } else if (!ukPhoneRegex.test(formData.phone.trim())) {
      errors.push("Please enter a valid phone number.");
    }

    if (!formData.postcode.trim()) {
      errors.push("Postcode is required.");
    } else if (!ukPostcodeRegex.test(formData.postcode.trim())) {
      errors.push("Please enter a valid postcode.");
    }

    if (
      !formData.resumeSelected ||
      !formData.resume ||
      formData.resume.length === 0
    ) {
      errors.push("Resume is required.");
    }

    if (!termsAccepted)
      errors.push("You must accept the terms and conditions.");

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateFormData(formData, termsAccepted);
    if (errors.length > 0) {
      alert(errors.join("\n"));
      return;
    }

    await JobseekerService.register(formData);
    alert("Your application has been submitted successfully!");
    onClose();
  };
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Full Name *
        </label>
        <input
          type="text"
          name="fullName"
          value={formData.fullName}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email Address *
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Phone Number *
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="+447xxxx110xx"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Postcode
        </label>
        <input
          type="text"
          name="postcode"
          value={formData.postcode}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Resume/CV *
        </label>
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
          <div className="space-y-1 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="flex text-sm text-gray-600">
              <span
                className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500"
                onClick={handleFileInput}
              >
                Upload a file
              </span>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
          </div>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
        {formData.resumeSelected && (
          <p className="mt-2 text-sm text-green-600 flex items-center">
            <CheckCircle className="h-4 w-4 mr-1" />
            Resume selected
          </p>
        )}
      </div>

      <div className="flex items-center">
        <input
          id="terms"
          type="checkbox"
          checked={termsAccepted}
          onChange={() => setTermsAccepted(!termsAccepted)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
          I agree to the{" "}
          <span className="text-blue-600 hover:underline cursor-pointer">
            terms and conditions
          </span>{" "}
          and{" "}
          <span className="text-blue-600 hover:underline cursor-pointer">
            privacy policy
          </span>
          .
        </label>
      </div>

      <div className="flex justify-end pt-4">
        <button
          onClick={onClose}
          className="mr-4 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="bg-indigo-500 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Submit Application
        </button>
      </div>
    </div>
  );
};

const JobDetail = ({ jobId }) => {
  const [isApplying, setIsApplying] = useState(false);
  const [jobData, setJobData] = useState({});
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); // Add this hook for navigation

  useEffect(() => {
    const jobDetails = async () => {
      const job = await JobService.getJob(jobId);
      if (!job) {
        navigate("/404");
      }
      setJobData(job);
      setCompany(job.company ?? {});
      setLoading(false);
    };
    jobDetails();
  }, [jobId]);

  let salary =
    "£" + jobData?.jobAdvert?.jobDetails?.salary?.exactAmount?.amount ?? 0;
  let sal_frequency =
    jobData?.jobAdvert?.jobDetails?.salary?.exactAmount?.frequency.value ??
    " - ";
  if (salary === 0) {
    salary = `£${jobData?.jobAdvert?.jobDetails?.salary?.range?.min?.toLocaleString()} - £${jobData?.jobAdvert?.jobDetails?.salary?.range?.max?.toLocaleString()}`;
  }

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <span className="text-gray-600">Loading job details...</span>
      </div>
    );
  }
  const parts = [];

  if (
    jobData?.jobAdvert?.basicInfo?.industry &&
    jobData.jobAdvert.basicInfo.industry !== "-"
  ) {
    parts.push(jobData.jobAdvert.basicInfo.industry);
  }

  const city = jobData?.jobAdvert?.basicInfo?.location?.details?.city;
  const postcode = jobData?.jobAdvert?.basicInfo?.location?.details?.postcode;

  // Check if city and postcode have actual values (not null, undefined, empty string, or "-")
  const hasValidCity = city && city !== "-" && city.trim() !== "";
  const hasValidPostcode =
    postcode && postcode !== "-" && postcode.trim() !== "";

  if (hasValidCity || hasValidPostcode) {
    const locationStr =
      hasValidCity && hasValidPostcode
        ? `${city}, ${postcode}`
        : hasValidCity
          ? city
          : postcode;
    parts.push(locationStr);
  }

  const jobType = capitalizeWords(
    jobData?.jobAdvert?.jobDetails?.jobType?.value ?? "",
  );
  if (jobType && jobType !== "-") {
    parts.push(jobType);
  }

  const subheading = parts.join(" • ");
  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <Header
        logoPath={company && company.logo ? API_BASE_URL + company.logo : null}
      />
      <HeaderBanner
        title={`${jobData?.jobAdvert?.basicInfo?.jobTitle}`}
        subtitle={subheading}
        logoUrl={company && company.logo ? API_BASE_URL + company.logo : null}
        category="Careers"
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Job Details */}

          <div className="lg:w-2/3">
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <a
                href={`/${jobData.company._id}/career`}
                className="float-right text-sm"
              >
                Back to Jobs
              </a>
              <JobDescriptionHtmlParser
                description={jobData?.jobAdvert?.description}
              />
            </div>

            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4">
                Required Qualifications
              </h2>
              {jobData &&
                jobData.jobAdvert &&
                jobData.jobAdvert.qualifications &&
                jobData.jobAdvert.qualifications.required ? (
                <ul className="list-disc pl-5 space-y-2 text-gray-700">
                  {jobData.jobAdvert.qualifications.required.map(
                    (req, index) => (
                      <li key={index}>{req}</li>
                    ),
                  )}
                </ul>
              ) : (
                <p className="text-gray-600">
                  No required qualifications specified.
                </p>
              )}
            </div>

            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4">
                Preferred Qualifications
              </h2>
              {jobData &&
                jobData.jobAdvert &&
                jobData.jobAdvert.qualifications &&
                jobData.jobAdvert.qualifications.preferred ? (
                <ul className="list-disc pl-5 space-y-2 text-gray-700">
                  {jobData.jobAdvert.qualifications.preferred.map(
                    (pref, index) => (
                      <li key={index}>{pref}</li>
                    ),
                  )}
                </ul>
              ) : (
                <p className="text-gray-600">
                  No preferred qualifications specified.
                </p>
              )}
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">
                Additional Requirements
              </h2>
              {jobData &&
                jobData.jobAdvert &&
                jobData.jobAdvert.qualifications &&
                jobData.jobAdvert.qualifications.additionalRequirements ? (
                <ul className="list-disc pl-5 space-y-2 text-gray-700">
                  {jobData.jobAdvert.qualifications.additionalRequirements.map(
                    (req, index) => (
                      <li key={index}>{req}</li>
                    ),
                  )}
                </ul>
              ) : (
                <p className="text-gray-600">
                  No additional requirements specified.
                </p>
              )}
            </div>
          </div>

          {/* Right Column - Summary Info */}
          <div className="lg:w-1/3">
            <div className="bg-white shadow rounded-lg p-6 mb-6 sticky top-4">
              <h2 className="text-xl font-semibold mb-4">Job Summary</h2>

              <div className="space-y-4">
                <div className="flex items-start">
                  <Briefcase className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Job Type</h3>
                    <p className="text-gray-600">
                      {jobData?.jobAdvert?.jobDetails?.jobType?.value ??
                        "No Job Type is mentioned"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CreditCard className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Salary</h3>
                    {jobData.jobAdvert ? (
                      <p className="text-gray-600">
                        {jobData.jobAdvert.jobDetails.salary.basedOnExperience
                          ? "Based on experience"
                          : `${salary} / ${sal_frequency}`}
                      </p>
                    ) : (
                      ""
                    )}
                  </div>
                </div>

                <div className="flex items-start">
                  <Calendar className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Start Date</h3>
                    {jobData.jobAdvert ? (
                      <p className="text-gray-600">
                        {jobData.jobAdvert.jobDetails.startDate.ASAP
                          ? "As soon as possible"
                          : new Date(
                            jobData.jobAdvert.jobDetails.startDate.date,
                          ).toLocaleDateString()}
                      </p>
                    ) : (
                      ""
                    )}
                  </div>
                </div>

                <div className="flex items-start">
                  <Clock className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Working Hours</h3>

                    {jobData.jobAdvert ? (
                      <p className="text-gray-600">
                        {`${jobData.jobAdvert.workingConditions.hours.defined.from} - ${jobData.jobAdvert.workingConditions.hours.defined.to}`}
                      </p>
                    ) : (
                      ""
                    )}
                  </div>
                </div>

                <div className="flex items-start">
                  <Award className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Experience</h3>

                    {jobData.jobAdvert ? (
                      <p className="text-gray-600">
                        {
                          jobData.jobAdvert.qualifications.experience
                            .minimumYears.value
                        }
                      </p>
                    ) : (
                      ""
                    )}
                  </div>
                </div>

                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Work Type</h3>

                    {jobData.jobAdvert ? (
                      <p className="text-gray-600">
                        {jobData.jobAdvert.qualifications.remoteType.value}
                      </p>
                    ) : (
                      ""
                    )}
                  </div>
                </div>

                <div className="flex items-start">
                  <FileText className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">
                      Required Documents
                    </h3>

                    {jobData.jobAdvert ? (
                      <ul className="text-gray-600">
                        {jobData.jobAdvert.documentRequirements.CVRequired && (
                          <li>CV/Resume</li>
                        )}
                        {jobData.jobAdvert.documentRequirements
                          .referencesRequired.required && (
                            <li>{`${jobData.jobAdvert.documentRequirements.referencesRequired.minimumReferences.value} References`}</li>
                          )}
                      </ul>
                    ) : (
                      ""
                    )}
                  </div>
                </div>

                <div className="flex items-start">
                  <User className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">
                      Number of Hires
                    </h3>
                    <p className="text-gray-600">
                      {jobData?.jobAdvert?.basicInfo?.numberOfHires}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Info className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Job Reference</h3>
                    <p className="text-gray-600">{jobData.jobReference}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsApplying(true)}
                className="mt-6 w-full bg-indigo-500 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-md shadow transition duration-150"
              >
                Apply Now
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Application Modal */}
      {isApplying && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">
                  Apply for {jobData.jobAdvert.basicInfo.jobTitle}
                </h2>
                <button
                  onClick={() => setIsApplying(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <ApplicationForm
                onClose={() => setIsApplying(false)}
                jobId={jobId}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetail;
